/**
 * LifeHomePage（3状態コンテナ）
 * 詳細設計書 §6.2 に対応。
 *
 * /life の 1 パスで「未出社 / 出社済み / 退社済み」を出し分ける。
 * マウント時に GET /my-seat/ で初期状態を判定する。
 */
import { useEffect, useState, useCallback } from 'react';
import '../styles/life.css';
import LifeHeader from '../components/LifeHeader';
import NotCheckedIn from '../components/NotCheckedIn';
import CheckedIn from '../components/CheckedIn';
import CheckedOut from '../components/CheckedOut';
import { getMySeat } from '../api/lifeApi';

/** 自席 API レスポンスから画面状態を判定 */
function resolveScreen(data) {
  if (!data || !data.has_seat) return 'not-checked-in';
  // is_active がない（退社済み）場合は checked-out
  // my-seat API は is_active を返す仕様
  if (data.is_active === false) return 'checked-out';
  return 'checked-in';
}

function LifeHomePage({ onNavigate }) {
  const [screen, setScreen] = useState(null); // null = ローディング中
  const [assignment, setAssignment] = useState(null);
  const [checkOutRecord, setCheckOutRecord] = useState(null);

  /** ページロード時に状態判定 */
  useEffect(() => {
    getMySeat()
      .then((res) => {
        const data = res?.data ?? null;
        setAssignment(data);
        setScreen(resolveScreen(data));
      })
      .catch(() => {
        // 404 = 未出社
        setScreen('not-checked-in');
      });
  }, []);

  /** 出社完了コールバック */
  const handleCheckIn = useCallback((data) => {
    // 出社直後は my-seat を再取得して最新情報を反映
    getMySeat()
      .then((res) => {
        setAssignment(res?.data ?? data);
        setScreen('checked-in');
      })
      .catch(() => {
        setAssignment(data);
        setScreen('checked-in');
      });
  }, []);

  /** 退社完了コールバック */
  const handleCheckOut = useCallback((checkOutData) => {
    setCheckOutRecord({
      seat_number: assignment?.seat_number,
      check_in_time: assignment?.check_in_time,
      check_out_time: checkOutData?.check_out_time ?? null,
    });
    setScreen('checked-out');
  }, [assignment]);

  /** 戻るボタン */
  const handleBack = useCallback(() => {
    if (onNavigate) onNavigate('/');
    else window.history.back();
  }, [onNavigate]);

  /** ポータルトップへ */
  const handleGoTop = useCallback(() => {
    if (onNavigate) onNavigate('/');
    else window.location.href = '/';
  }, [onNavigate]);

  return (
    <div className="life-root">
      <LifeHeader onBack={handleBack} />

      <main className="life-page">
        {/* ローディング */}
        {screen === null && (
          <div style={{ paddingTop: 80, textAlign: 'center' }}>
            <span className="life-spinner life-spinner--dark" aria-label="読み込み中" />
          </div>
        )}

        {/* 未出社 */}
        {screen === 'not-checked-in' && (
          <NotCheckedIn onCheckIn={handleCheckIn} />
        )}

        {/* 出社済み */}
        {screen === 'checked-in' && (
          <CheckedIn assignment={assignment} onCheckOut={handleCheckOut} />
        )}

        {/* 退社済み */}
        {screen === 'checked-out' && (
          <CheckedOut record={checkOutRecord ?? assignment} onBack={handleGoTop} />
        )}
      </main>
    </div>
  );
}

export default LifeHomePage;
